import { useMemo, useEffect, useRef } from 'react';
import {
  ServWithMods,
  ServiceWithPricing,
  EquipmentQuantity,
  QuoteFullyPopulated,
  RequiredInfoValue,
} from '@/util/types/serverTypes';
import { AddressForScheduler } from '@/util/types/globalTypes';
import {
  Bundle,
  DiscountCode,
} from '@/util/types/serverTypeCollection/discounts';
import { calculateServicePrices } from './calculateServicePrices';
import { extractEquipmentAdjustments } from './extractEquipmentAdjustments';
import { mergeEquipmentWithOverrides } from './mergeEquipmentWithOverrides';
import { applyPriceOverrides } from './applyPriceOverrides';
import { annotateBundleMetadata } from './annotateBundleMetadata';

export interface UsePriceCalculationParams {
  services: ServWithMods[];
  selectedServiceIds: string[];
  address: AddressForScheduler | null;
  serviceAreaCost?: number;
  requestedInspector?: boolean;
  requiredInfoValues?: RequiredInfoValue[];
  equipmentQuantities: EquipmentQuantity[];
  inspectorIds?: string[];
  /** IDs of the service areas whose polygon contains the property (for service-area modifier conditions). */
  serviceAreaIds?: string[];
  bundle?: Bundle | null;
  discountCode?: DiscountCode | null;
  appliedDiscountServiceIds?: string[];
  previousServices?: ServiceWithPricing[];
  quote?: QuoteFullyPopulated;
  onEquipmentChange?: (equipment: EquipmentQuantity[]) => void;
}

export interface UsePriceCalculationResult {
  services: ServiceWithPricing[];
  totalPrice: number;
  totalDuration: number;
  expandedAreaPrice: number;
}

/**
 * Calculates service prices with two-pass system for equipment dependencies
 * 
 * ## Two-Pass System
 * The price calculation uses a two-pass approach due to circular dependencies:
 * 
 * **Pass 1**: Calculate prices with current equipment
 * - Service modifiers determine equipment needs (e.g., "need 2 radon canisters if sqft > 3000")
 * - Extract equipment adjustments from modifier results
 * 
 * **Pass 2**: Recalculate with updated equipment (if changed)
 * - Use updated equipment quantities for accurate pricing
 * - Some modifiers add cost based on equipment count (e.g., "$50 per canister")
 * 
 * ## Key Features
 * - Respects manual equipment overrides (via `manualChange` flag)
 * - Preserves price overrides across recalculations
 * - Applies bundle and discount code metadata
 * - Optimized: skips second pass when equipment unchanged
 * - Calls `onEquipmentChange` callback when equipment updates
 * 
 * @param params - Configuration for price calculation
 * @returns Calculated services with pricing, totals, and expanded area price
 */
export function usePriceCalculation(
  params: UsePriceCalculationParams
): UsePriceCalculationResult {
  const {
    services,
    selectedServiceIds,
    address,
    serviceAreaCost = 0,
    requestedInspector = false,
    requiredInfoValues = [],
    equipmentQuantities,
    inspectorIds = [],
    serviceAreaIds = [],
    bundle = null,
    discountCode = null,
    appliedDiscountServiceIds = [],
    previousServices,
    quote,
    onEquipmentChange,
  } = params;

  // Track merged equipment for useEffect callback
  const mergedEquipmentRef = useRef<EquipmentQuantity[]>([]);
  const hasEquipmentChangesRef = useRef(false);
  const lastCalledEquipmentRef = useRef<EquipmentQuantity[]>([]);

  const result = useMemo(() => {
    // Validation: require selected services
    if (selectedServiceIds.length === 0) {
      return {
        services: [],
        totalPrice: 0,
        totalDuration: 0,
        expandedAreaPrice: 0,
      };
    }

    // Build selected services with isPrimary flag (first service that can be primary gets isPrimary)
    const selectedServicesOrdered = selectedServiceIds
      .map((id) => services.find((s) => s._id === id))
      .filter((s): s is NonNullable<typeof s> => s !== undefined);
    const firstPrimaryIndex = selectedServicesOrdered.findIndex(
      (s) => s.canBePrimary !== false
    );
    const selectedServicesFinal = selectedServicesOrdered.map((service, i) => ({
      ...service,
      isPrimary: firstPrimaryIndex >= 0 && i === firstPrimaryIndex,
    }));

    if (selectedServicesFinal.length === 0) {
      return {
        services: [],
        totalPrice: 0,
        totalDuration: 0,
        expandedAreaPrice: serviceAreaCost || 0,
      };
    }

    // If no address yet, return services without pricing (modifiers need property details)
    if (!address) {
      const baseServices: ServiceWithPricing[] = selectedServicesFinal.map(
        (service) => ({
          ...service,
          totalPrice: 0,
          preTaxAmount: 0,
          totalDuration: service.duration,
        })
      );

      // Apply price overrides from quote even without address (for editing existing quotes)
      const servicesWithOverrides = applyPriceOverrides(
        baseServices,
        previousServices,
        quote
      );

      // If services have price overrides (from quote), calculate totals
      // Otherwise, totals remain 0 until property is entered
      const totalPrice = servicesWithOverrides.reduce(
        (sum, s) => sum + (s.priceOverride ?? 0),
        0
      );

      const totalDuration = servicesWithOverrides.reduce(
        (sum, s) => sum + (s.autoEvents?.length ? 0 : s.totalDuration || 0),
        0
      );

      return {
        services: servicesWithOverrides,
        totalPrice,
        totalDuration,
        expandedAreaPrice: 0,
      };
    }

    // PASS 1: Calculate with current equipment
    const firstPass = calculateServicePrices(
      selectedServicesFinal,
      address,
      serviceAreaCost,
      requestedInspector,
      requiredInfoValues,
      equipmentQuantities,
      selectedServiceIds,
      inspectorIds,
      {
        bundle,
        discountCode: discountCode
          ? { ...discountCode, _serviceIds: appliedDiscountServiceIds }
          : null,
      },
      serviceAreaIds
    );

    // Extract equipment adjustments from first pass
    const adjustments = extractEquipmentAdjustments(firstPass);

    // Merge with user overrides (respects manualChange flag)
    const { merged, hasChanges } = mergeEquipmentWithOverrides(
      adjustments,
      equipmentQuantities
    );

    // Store equipment changes in refs for useEffect to handle
    mergedEquipmentRef.current = merged;
    hasEquipmentChangesRef.current = hasChanges;

    // PASS 2: Recalculate if equipment changed
    let calculatedServices = hasChanges
      ? calculateServicePrices(
          selectedServicesFinal,
          address,
          serviceAreaCost,
          requestedInspector,
          requiredInfoValues,
          merged,
          selectedServiceIds,
          inspectorIds,
          {
            bundle,
            discountCode: discountCode
              ? { ...discountCode, _serviceIds: appliedDiscountServiceIds }
              : null,
          },
          serviceAreaIds
        )
      : firstPass;

    // Apply price overrides (from previous services or quote)
    calculatedServices = applyPriceOverrides(
      calculatedServices,
      previousServices,
      quote
    );

    // Annotate with bundle/discount metadata
    calculatedServices = annotateBundleMetadata(
      calculatedServices,
      bundle,
      discountCode,
      selectedServiceIds
    );

    // Calculate totals
    const totalPrice = calculatedServices.reduce(
      (sum, s) => sum + (s.priceOverride ?? (s.totalPrice || 0)),
      0
    );

    const totalDuration = calculatedServices.reduce(
      (sum, s) => sum + (s.autoEvents?.length ? 0 : s.totalDuration || 0),
      0
    );

    return {
      services: calculatedServices,
      totalPrice,
      totalDuration,
      expandedAreaPrice: serviceAreaCost || 0,
    };
  }, [
    services,
    selectedServiceIds,
    address,
    serviceAreaCost,
    requestedInspector,
    requiredInfoValues,
    equipmentQuantities,
    inspectorIds,
    serviceAreaIds,
    bundle,
    discountCode,
    appliedDiscountServiceIds,
    previousServices,
    quote,
  ]);

  // Handle equipment changes in useEffect to avoid updating state during render
  useEffect(() => {
    if (hasEquipmentChangesRef.current && onEquipmentChange) {
      // Check if equipment actually changed from last callback
      const current = mergedEquipmentRef.current;
      const last = lastCalledEquipmentRef.current;
      
      const isDifferent =
        current.length !== last.length ||
        current.some((eq, idx) => {
          const lastEq = last[idx];
          return (
            !lastEq ||
            eq._equipmentCategoryId !== lastEq._equipmentCategoryId ||
            eq.value !== lastEq.value ||
            eq.manualChange !== lastEq.manualChange
          );
        });
      
      if (isDifferent) {
        onEquipmentChange(current);
        lastCalledEquipmentRef.current = current;
      }
      hasEquipmentChangesRef.current = false; // Reset flag after checking
    }
  }, [result, onEquipmentChange]);

  return result;
}

